import React, { useState, useEffect } from 'react';
import { ChevronLeft, Plus, Minus, PiggyBank, TrendingUp, Calendar, Zap, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import FedaPayEpargneButton from '../UI/FedaPayEpargneButton';
import { BACKEND_URL } from '../../config/backend';

// ── helpers ─────────────────────────────────────────────────────────────────
const calcStats = (montant, frequence, nombreMois) => {
  const deps = Math.floor((nombreMois * 30) / parseInt(frequence));
  const totalDepose = montant * deps;
  const interets = Math.round(totalDepose * 0.05 * nombreMois);
  const total = totalDepose + interets;
  return { deps, totalDepose, interets, total };
};

// ── Component ────────────────────────────────────────────────────────────────
const ABEpargne = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // eslint-disable-next-line no-unused-vars
  const [currentPage, setCurrentPage] = useState('config');
  const [planConfig, setPlanConfig] = useState({ montant: 500, frequence: '5', nombreMois: 6 });
  const [showPayment, setShowPayment] = useState(false);
  const [loading, setLoading] = useState(false);

  const stats = calcStats(planConfig.montant, planConfig.frequence, planConfig.nombreMois);

  // ── Retour après paiement FedaPay ─────────────────────────────────────────
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const reference = urlParams.get('reference');
    const status = urlParams.get('status');
    if (reference && status === 'approved') {
      window.history.replaceState({}, document.title, window.location.pathname);
      navigate(`/ab-epargne/retour?reference=${reference}&status=approved`);
    }
  }, [navigate]);

  // ── Vérifie si un plan actif existe déjà ─────────────────────────────────
  useEffect(() => {
    const fetchActivePlan = async () => {
      if (!user) { setLoading(false); return; }
      try {
        setLoading(true);
        const response = await fetch(`${BACKEND_URL}/api/savings/plan-status?userId=${user.id}`);
        const result = await response.json();
        if (result.success && result.plan) {
          const isPersonalized =
            result.plan.personalized_at &&
            result.plan.plan_name?.trim() !== '' &&
            result.plan.plan_name?.trim() !== 'Plan Épargne' &&
            result.plan.goal;
          if (!isPersonalized) {
            navigate(`/ab-epargne/personalize/${result.plan.id}`, { replace: true });
          } else {
            navigate(`/ab-epargne/plan/${result.plan.id}`, { replace: true });
          }
        }
      } catch (err) {
        console.error('[AB_EPARGNE]', err);
      } finally {
        setLoading(false);
      }
    };
    fetchActivePlan();
  }, [user, navigate]);

  const inc = (key, step, min) =>
    setPlanConfig(p => ({ ...p, [key]: Math.max(min, p[key] + step) }));

  // ── Config Page ───────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-emerald-50">

      {/* Loading overlay */}
      {loading && (
        <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-gray-600 font-medium">Vérification du plan...</p>
          </div>
        </div>
      )}

      {/* ── Top bar ── */}
      <div className="w-full max-w-2xl mx-auto px-4 pt-5 pb-2 flex items-center justify-between">
        <button
          onClick={() => navigate('/dashboard')}
          className="w-10 h-10 bg-white rounded-full shadow-sm flex items-center justify-center hover:shadow-md transition-shadow"
        >
          <ChevronLeft size={20} className="text-gray-700" />
        </button>
        <button
          onClick={() => navigate('/ab-epargne/historique')}
          className="text-sm text-blue-600 font-medium hover:underline"
        >
          Historique
        </button>
      </div>

      {/* ── Main card ── */}
      <div className="flex justify-center px-4 pb-10 pt-2">
        <div className="w-full max-w-md">

          {/* Hero */}
          <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 rounded-3xl px-6 pt-7 pb-12 mb-[-2rem] shadow-2xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                <PiggyBank size={26} className="text-white" />
              </div>
              <div>
                <p className="text-blue-200 text-xs font-semibold uppercase tracking-widest">AB Épargne</p>
                <h1 className="text-white text-xl font-bold">Créer mon plan</h1>
              </div>
            </div>

            {/* Projected total */}
            <div className="text-center mt-2 mb-1">
              <p className="text-blue-200 text-sm mb-1">Objectif estimé</p>
              <p className="text-white text-4xl font-extrabold tracking-tight">
                {stats.total.toLocaleString()} <span className="text-2xl font-semibold">F</span>
              </p>
              <p className="text-blue-200 text-xs mt-1">dont {stats.interets.toLocaleString()} F d'intérêts (5%/mois)</p>
            </div>
          </div>

          {/* Form card */}
          <div className="bg-white rounded-3xl shadow-xl px-6 pt-8 pb-6 relative z-10">

            {/* ── Montant ── */}
            <div className="mb-6">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Montant par dépôt
              </label>
              <div className="flex items-center bg-gray-50 border border-gray-200 rounded-2xl p-3 gap-3">
                <button
                  onClick={() => inc('montant', -100, 500)}
                  className="w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center hover:bg-gray-50 active:scale-95 transition-all border border-gray-100"
                >
                  <Minus size={16} className="text-gray-600" />
                </button>
                <div className="flex-1 flex items-center justify-center gap-1.5">
                  <input
                    type="number"
                    min="500"
                    step="100"
                    value={planConfig.montant}
                    onChange={e => setPlanConfig(p => ({ ...p, montant: Math.max(500, parseInt(e.target.value) || 500) }))}
                    onFocus={e => e.target.select()}
                    className="text-2xl font-bold text-gray-900 text-center bg-transparent border-none outline-none w-28"
                  />
                  <span className="text-gray-400 font-semibold text-lg">F</span>
                </div>
                <button
                  onClick={() => inc('montant', 100, 500)}
                  className="w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center hover:bg-gray-50 active:scale-95 transition-all border border-gray-100"
                >
                  <Plus size={16} className="text-gray-600" />
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1.5 ml-1">Minimum 500 F CFA</p>
            </div>

            {/* ── Fréquence ── */}
            <div className="mb-6">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Fréquence de dépôt
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { val: '5', label: 'Tous les 5 jours', icon: <Zap size={14} /> },
                  { val: '10', label: 'Tous les 10 jours', icon: <Calendar size={14} /> },
                ].map(({ val, label, icon }) => (
                  <button
                    key={val}
                    onClick={() => setPlanConfig(p => ({ ...p, frequence: val }))}
                    className={`flex items-center justify-center gap-2 py-3.5 px-3 rounded-2xl border-2 font-semibold text-sm transition-all duration-200 ${
                      planConfig.frequence === val
                        ? 'bg-blue-50 border-blue-500 text-blue-700 shadow-md scale-[1.02]'
                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {icon}
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Durée ── */}
            <div className="mb-6">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Durée du plan
              </label>
              <div className="flex items-center bg-gray-50 border border-gray-200 rounded-2xl p-3 gap-3">
                <button
                  onClick={() => inc('nombreMois', -1, 1)}
                  className="w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center hover:bg-gray-50 active:scale-95 transition-all border border-gray-100"
                >
                  <Minus size={16} className="text-gray-600" />
                </button>
                <div className="flex-1 flex items-center justify-center gap-2">
                  <span className="text-2xl font-bold text-gray-900">{planConfig.nombreMois}</span>
                  <span className="text-gray-400 font-semibold text-lg">mois</span>
                </div>
                <button
                  onClick={() => inc('nombreMois', 1, 1)}
                  className="w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center hover:bg-gray-50 active:scale-95 transition-all border border-gray-100"
                >
                  <Plus size={16} className="text-gray-600" />
                </button>
              </div>
            </div>

            {/* ── Résumé ── */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-4 mb-6 border border-blue-100">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={16} className="text-blue-600" />
                <span className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Résumé du plan</span>
              </div>
              <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                <div className="flex justify-between col-span-2 py-1 border-b border-blue-100">
                  <span className="text-gray-500">Nombre de dépôts</span>
                  <span className="font-semibold text-gray-800">{stats.deps}</span>
                </div>
                <div className="flex justify-between col-span-2 py-1 border-b border-blue-100">
                  <span className="text-gray-500">Total déposé</span>
                  <span className="font-semibold text-gray-800">{stats.totalDepose.toLocaleString()} F</span>
                </div>
                <div className="flex justify-between col-span-2 py-1 border-b border-blue-100">
                  <span className="text-gray-500">Intérêts estimés</span>
                  <span className="font-semibold text-green-600">+{stats.interets.toLocaleString()} F</span>
                </div>
                <div className="flex justify-between col-span-2 pt-1">
                  <span className="font-bold text-gray-700">Objectif total</span>
                  <span className="font-extrabold text-blue-600 text-base">{stats.total.toLocaleString()} F</span>
                </div>
              </div>
            </div>

            {/* ── CTA ── */}
            <button
              onClick={() => setShowPayment(true)}
              className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold text-base rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200 active:scale-[0.98]"
            >
              Créer mon plan · 1 000 F
            </button>

            <p className="text-center text-xs text-gray-400 mt-3">
              Frais de création uniques · Paiement sécurisé via FedaPay
            </p>
          </div>
        </div>
      </div>

      {/* ── Payment Modal ── */}
      {showPayment && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white w-full sm:max-w-sm sm:rounded-3xl rounded-t-3xl shadow-2xl overflow-hidden">

            {/* Header */}
            <div className="relative bg-gradient-to-br from-blue-600 to-indigo-700 px-6 pt-6 pb-10">
              <button
                onClick={() => setShowPayment(false)}
                className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
              >
                <X size={20} className="text-white" />
              </button>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <PiggyBank size={22} className="text-white" />
                </div>
                <div>
                  <p className="text-blue-200 text-xs font-medium">Frais de création</p>
                  <h3 className="text-white font-bold text-lg">Activer mon plan</h3>
                </div>
              </div>
              <div className="text-center">
                <p className="text-blue-200 text-sm mb-1">Montant à payer</p>
                <p className="text-white text-4xl font-extrabold">1 000 <span className="text-2xl font-semibold">F CFA</span></p>
              </div>
            </div>

            {/* Body */}
            <div className="-mt-4 bg-white rounded-t-3xl px-6 pt-6 pb-8 space-y-4">
              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
                <p className="text-xs text-blue-700 font-semibold uppercase tracking-wider mb-2">Votre plan</p>
                <div className="space-y-1.5 text-sm text-gray-700">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Dépôt</span>
                    <span className="font-semibold">{planConfig.montant.toLocaleString()} F / {planConfig.frequence} jours</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Durée</span>
                    <span className="font-semibold">{planConfig.nombreMois} mois</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Objectif</span>
                    <span className="font-bold text-blue-600">{stats.total.toLocaleString()} F</span>
                  </div>
                </div>
              </div>

              <FedaPayEpargneButton planConfig={planConfig} />

              <button
                onClick={() => setShowPayment(false)}
                className="w-full py-3 text-gray-500 font-medium text-sm hover:text-gray-700 transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ABEpargne;
